import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Accordion, AccordionDetails, AccordionSummary, Alert, AlertTitle, Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
    FormControlLabel, IconButton, LinearProgress, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, ToggleButton,
    ToggleButtonGroup, Tooltip, Typography
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import bulkAudioStore from '../../Mobx/BulkAudioStore';
import { formatError } from '../../Mobx/AudioCatalog';
import {
    activeItemIndex, formatClock, formatDateTime, formatNumber, groupFacts, groupItemSeverity, groupItemStatusLabel, groupTitle, markKey, markSnapshot,
    markedWordsText, parseMarks, seekSeconds, sortedMarks, wordsCount
} from './bulkAudioForm';
import { ConfirmDialog, chipColor } from './BulkRunJobs';

/**
 * The recordings of the groups of a group run (SCRUM-1882), for listening: the server keeps the WHOLE recording of every group, with the
 * list of its words and the time of each one in it. Here the admin opens a group, listens to it from the beginning to the end (the word
 * that is heard is lit), jumps to any word, and marks the words that sound wrong. The marks stay in this browser (per run) and can be copied
 * as a table to hand back - the files that hold a wrong word are then made again, one by one.
 *
 * The link to a recording is valid for an hour. It is asked for when a group is opened (the server signs it then), kept while the group is
 * open - a link that changes while the audio plays would restart it - and asked for again when it is about to expire, or does not load.
 */

const LINK_MAX_AGE_MS = 50 * 60 * 1000;
const RATES = [1, 1.25, 1.5];
const MARKS_KEY = 'lexillon.bulkAudio.marks.';

/** The marks of a run that this browser kept ({} when it kept none, or does not keep anything) */
const readMarks = (jobId) => {
    try {
        return parseMarks(window.localStorage.getItem(`${MARKS_KEY}${jobId}`));
    } catch {
        return {};
    }
};

const writeMarks = (jobId, marks) => {
    try {
        if (Object.keys(marks).length === 0) window.localStorage.removeItem(`${MARKS_KEY}${jobId}`);
        else window.localStorage.setItem(`${MARKS_KEY}${jobId}`, JSON.stringify(marks));
    } catch {
        // the browser keeps nothing (a private window, no room left): the marks live until the page is closed
    }
};

/**
 * A line of facts: "Callirrhoe · 60 מילים · אורך 0:18.3 · 286 KB". The line follows the direction of the page (from the right), and every fact is
 * isolated, so that a fact in English (the name of the voice, the size) does not turn the order of the others around. With dir="auto" the first
 * letter of the line decides its direction - and a line that begins with the name of a voice is then read from the left, with its Hebrew parts
 * and its numbers in the wrong places.
 */
const Facts = ({ facts }) => facts.filter(Boolean).map((fact, index) => (
    <React.Fragment key={index}>
        {index > 0 && ' · '}
        <bdi style={{ whiteSpace: 'nowrap' }}>{fact}</bdi>
    </React.Fragment>
));

/** One word of a group. It is rendered again only when it changes (a recording has up to 400 words, and the lit word changes every second). */
const WordRow = React.memo(({ item, group, index, active, marked, onToggleMark, onPlay, registerRow }) => (
    <TableRow
        ref={(node) => registerRow(index, node)}
        selected={active}
        hover
        data-testid={`bulk-word-row-${item.n}`}
        data-active={active ? 'true' : undefined}
        data-status={item.status}
    >
        <TableCell padding="checkbox">
            <Checkbox
                checked={marked}
                onChange={() => onToggleMark(group, item)}
                inputProps={{ 'aria-label': `סימון המילה ${item.text} כמילה שנשמעת לא טוב`, 'data-testid': `bulk-word-mark-${item.n}` }}
            />
        </TableCell>
        <TableCell padding="checkbox">
            <Tooltip title="השמעה מהמילה הזאת">
                <IconButton size="small" onClick={() => onPlay(item)} aria-label={`השמעה מהמילה ${item.text}`} data-testid={`bulk-word-play-${item.n}`}>
                    <PlayArrowIcon fontSize="small" />
                </IconButton>
            </Tooltip>
        </TableCell>
        <TableCell>{item.n}</TableCell>
        <TableCell sx={{ fontWeight: active ? 'bold' : undefined }}><bdi>{item.text}</bdi></TableCell>
        <TableCell sx={{ fontFamily: 'monospace' }}><bdi>{item.fileName}</bdi></TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap' }}><bdi>{formatClock(item.startMs)}</bdi></TableCell>
        <TableCell>
            <Chip size="small" color={chipColor(groupItemSeverity(item.status))} label={groupItemStatusLabel(item.status)} />
            {item.note && <Typography variant="caption" color="text.secondary" component="div" dir="auto">{item.note}</Typography>}
        </TableCell>
    </TableRow>
));

/** One open group: its player and its words */
const GroupPlayer = ({ jobId, number, hasNext, marks, onToggleMark, onNext }) => {
    const [detail, setDetail] = useState(null);
    const [error, setError] = useState(null);
    const [linkError, setLinkError] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);
    const [follow, setFollow] = useState(true);
    const [rate, setRate] = useState(1);
    const audioRef = useRef(null);
    const tableRef = useRef(null);
    const rowRefs = useRef(new Map());
    const loadedAtRef = useRef(0);
    const pendingRef = useRef(null); // { seconds, play }: what the player does when it has a new link
    const aliveRef = useRef(true);

    useEffect(() => {
        aliveRef.current = true;
        return () => { aliveRef.current = false; };
    }, []);

    const load = useCallback(async () => {
        const result = await bulkAudioStore.fetchGroup(jobId, number);
        if (!aliveRef.current) return false;
        if (result.error) {
            setError(result.error);
            return false;
        }
        setError(null);
        setLinkError(false);
        loadedAtRef.current = Date.now();
        setDetail({ group: result.group, items: result.items });
        return true;
    }, [jobId, number]);

    useEffect(() => {
        load();
    }, [load]);

    const registerRow = useCallback((index, node) => {
        if (node) rowRefs.current.set(index, node);
        else rowRefs.current.delete(index);
    }, []);

    // The lit word stays in view while the group plays (unless the admin turned it off to scroll by himself)
    useEffect(() => {
        if (!follow || activeIndex < 0) return;
        const container = tableRef.current;
        const row = rowRefs.current.get(activeIndex);
        if (!container || !row) return;
        const box = container.getBoundingClientRect();
        const line = row.getBoundingClientRect();
        const header = container.querySelector('thead')?.getBoundingClientRect().height ?? 0;
        if (line.top < box.top + header) container.scrollTop -= box.top + header - line.top + 4;
        else if (line.bottom > box.bottom) container.scrollTop += line.bottom - box.bottom + 4;
    }, [activeIndex, follow]);

    const applyRate = (audio, value) => {
        audio.defaultPlaybackRate = value;
        audio.playbackRate = value;
    };

    const changeRate = (event, value) => {
        if (value === null) return;
        setRate(value);
        if (audioRef.current) applyRate(audioRef.current, value);
    };

    // A new link replaces the old one: the player goes back to the place it was at (and plays, when it was asked to)
    const handleLoaded = (event) => {
        const audio = event.currentTarget;
        applyRate(audio, rate);
        const pending = pendingRef.current;
        if (!pending) return;
        pendingRef.current = null;
        audio.currentTime = pending.seconds;
        if (pending.play) audio.play().catch(() => {});
    };

    const handleTime = (event) => {
        if (!detail) return;
        const index = activeItemIndex(detail.items, event.currentTarget.currentTime);
        setActiveIndex((current) => (current === index ? current : index));
    };

    const askForNewLink = async () => {
        pendingRef.current = { seconds: audioRef.current?.currentTime ?? 0, play: false };
        await load();
    };

    const playFrom = useCallback(async (item) => {
        const audio = audioRef.current;
        if (!audio) return;
        const seconds = seekSeconds(item.startMs);
        if (Date.now() - loadedAtRef.current > LINK_MAX_AGE_MS) {
            pendingRef.current = { seconds, play: true };
            await load();
            return;
        }
        audio.currentTime = seconds;
        try {
            await audio.play();
        } catch {
            // the browser did not start it: the controls of the player are there
        }
    }, [load]);

    if (error) {
        return (
            <Alert severity="error" action={<Button color="inherit" size="small" onClick={load}>ניסיון חוזר</Button>} data-testid="bulk-group-error">
                <AlertTitle>טעינת ההקלטה נכשלה</AlertTitle>
                <Box dir="auto">{formatError(error)}</Box>
            </Alert>
        );
    }
    if (!detail) return <LinearProgress aria-label="טעינת ההקלטה" />;

    const { group, items } = detail;
    return (
        <Box data-testid="bulk-group-player">
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }} data-testid="bulk-group-facts-open">
                <Facts
                    facts={[
                        ...groupFacts(group),
                        group.firstSeq != null ? `הקבצים ${formatNumber(group.firstSeq)}-${formatNumber(group.lastSeq)} של ההרצה` : null,
                        group.savedAt ? <>נשמרה <bdi dir="ltr">{formatDateTime(group.savedAt)}</bdi></> : null,
                    ]}
                />
            </Typography>

            {group.audioUrl ? (
                <audio
                    ref={audioRef}
                    controls
                    preload="metadata"
                    src={group.audioUrl}
                    style={{ width: '100%' }}
                    onLoadedMetadata={handleLoaded}
                    onTimeUpdate={handleTime}
                    onSeeked={handleTime}
                    onError={() => setLinkError(true)}
                    data-testid="bulk-group-audio"
                />
            ) : (
                <Alert severity="warning" data-testid="bulk-group-no-link">
                    אי אפשר היה להכין קישור להאזנה להקלטה הזאת. רשימת המילים והזמנים כן נשמרה.
                </Alert>
            )}
            {linkError && (
                <Alert severity="warning" sx={{ mt: 1 }} action={<Button color="inherit" size="small" onClick={askForNewLink}>קישור חדש</Button>} data-testid="bulk-group-link-error">
                    ההקלטה לא נטענה. ייתכן שהקישור פג (הוא תקף שעה אחת).
                </Alert>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', my: 1 }}>
                <ToggleButtonGroup exclusive size="small" value={rate} onChange={changeRate} aria-label="מהירות ההשמעה">
                    {RATES.map((value) => <ToggleButton key={value} value={value} data-testid={`bulk-group-rate-${value}`}>{`${value}×`}</ToggleButton>)}
                </ToggleButtonGroup>
                <FormControlLabel
                    control={<Switch size="small" checked={follow} onChange={() => setFollow(!follow)} inputProps={{ 'data-testid': 'bulk-group-follow' }} />}
                    label="הרשימה עוקבת אחרי ההשמעה"
                />
                {hasNext && <Button size="small" onClick={onNext} data-testid="bulk-group-next">לקבוצה הבאה</Button>}
            </Box>

            <TableContainer ref={tableRef} sx={{ maxHeight: 420 }}>
                <Table size="small" stickyHeader aria-label="המילים של ההקלטה">
                    <TableHead>
                        <TableRow>
                            {['סימון', 'השמעה', '#', 'מילה', 'שם הקובץ', 'מקום', 'מצב'].map((header) => (
                                <TableCell key={header} sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>{header}</TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody data-testid="bulk-group-words">
                        {items.map((item, index) => (
                            <WordRow
                                key={item.n}
                                item={item}
                                group={group}
                                index={index}
                                active={index === activeIndex}
                                marked={Boolean(marks[markKey(group.number, item.n)])}
                                onToggleMark={onToggleMark}
                                onPlay={playFrom}
                                registerRow={registerRow}
                            />
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

/** The marked words: the table to copy */
const MarkedDialog = ({ open, text, onClose }) => {
    const [copied, setCopied] = useState(null); // true | false | null

    useEffect(() => {
        if (!open) setCopied(null);
    }, [open]);

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
        } catch {
            setCopied(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>המילים שסימנתי</DialogTitle>
            <DialogContent dividers data-testid="bulk-marked-dialog">
                <Typography variant="body2" sx={{ mb: 1 }}>
                    זו טבלה (העמודות מופרדות בטאב): הרצה, ובכל שורה מילה, שם הקובץ והמקום שלה בהקלטה. מעתיקים אותה ומעבירים הלאה, והקבצים של המילים האלה יוכנו מחדש אחד אחד.
                </Typography>
                <TextField
                    multiline
                    fullWidth
                    minRows={6}
                    maxRows={16}
                    value={text}
                    // the header and every row read from the right, like the rest of the page (with dir="auto" each line follows its own first letter)
                    inputProps={{ readOnly: true, dir: 'rtl', style: { fontFamily: 'monospace', unicodeBidi: 'embed' }, 'data-testid': 'bulk-marked-text' }}
                    onFocus={(event) => event.target.select()}
                />
                {copied === true && <Alert severity="success" sx={{ mt: 1 }} data-testid="bulk-marked-copied">הועתק.</Alert>}
                {copied === false && <Alert severity="warning" sx={{ mt: 1 }} data-testid="bulk-marked-copy-failed">הדפדפן לא איפשר להעתיק אוטומטית. לוחצים בתוך הטבלה, בוחרים הכול (Ctrl+A) ומעתיקים (Ctrl+C).</Alert>}
            </DialogContent>
            <DialogActions>
                <Button onClick={copy} variant="contained" data-testid="bulk-marked-copy">העתקה</Button>
                <Button onClick={onClose}>סגירה</Button>
            </DialogActions>
        </Dialog>
    );
};

/**
 * The recordings of a group run. `job` is the run as the server sends it (job.groupStats.recordings says how many recordings are kept - when
 * it grows, the list is loaded again).
 */
const BulkRunGroupRecordings = ({ jobId, job }) => {
    const [list, setList] = useState({ groups: [], total: 0, error: null, loaded: false });
    const [expanded, setExpanded] = useState(null);
    const [marks, setMarks] = useState(() => readMarks(jobId));
    const [showMarked, setShowMarked] = useState(false);
    const [clearing, setClearing] = useState(false);
    const firstWrite = useRef(true);
    const recordings = job?.groupStats?.recordings ?? 0;
    const recordingsFailed = job?.groupStats?.recordingsFailed ?? 0;

    const loadList = useCallback(async () => {
        const result = await bulkAudioStore.fetchGroups(jobId);
        setList({ groups: result.groups, total: result.total, error: result.error, loaded: true });
    }, [jobId]);

    useEffect(() => {
        loadList();
    }, [loadList, recordings]);

    useEffect(() => {
        if (firstWrite.current) {
            firstWrite.current = false;
            return;
        }
        writeMarks(jobId, marks);
    }, [jobId, marks]);

    const toggleMark = useCallback((group, item) => {
        const key = markKey(group.number, item.n);
        setMarks((current) => {
            const next = { ...current };
            if (next[key]) delete next[key];
            else next[key] = markSnapshot(group, item);
            return next;
        });
    }, []);

    const marked = sortedMarks(marks);
    const markedIn = (number) => marked.filter((mark) => mark.g === number).length;
    const { groups } = list;

    return (
        <Box sx={{ mt: 3 }} data-testid="bulk-groups-card">
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>הקלטות הקבוצות (להאזנה)</Typography>
                <Button size="small" variant="outlined" onClick={loadList} data-testid="bulk-groups-refresh">רענון הרשימה</Button>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                לכל קבוצה נשמרה ההקלטה המלאה שהתקבלה, לפני החיתוך לקבצים. פותחים קבוצה, מאזינים לה מההתחלה ועד הסוף (המילה שנשמעת מודגשת), וצובעים בסימון כל מילה שנשמעת לא טוב.
                ההקלטות פרטיות ולא מופיעות באתר.
            </Typography>

            {list.error && (
                <Alert severity="error" sx={{ mb: 1 }} data-testid="bulk-groups-error">
                    <AlertTitle>טעינת ההקלטות נכשלה</AlertTitle>
                    <Box dir="auto">{formatError(list.error)}</Box>
                </Alert>
            )}
            {!list.loaded && !list.error && <LinearProgress aria-label="טעינת ההקלטות" />}
            {list.loaded && !list.error && groups.length === 0 && (
                <Alert severity="info" data-testid="bulk-groups-empty">
                    {job?.status === 'running'
                        ? 'ההקלטה של כל קבוצה מופיעה כאן אחרי שהקבוצה מסתיימת.'
                        : 'להרצה הזאת לא נשמרו הקלטות של קבוצות.'}
                </Alert>
            )}
            {recordingsFailed > 0 && (
                <Alert severity="warning" sx={{ mb: 1 }} data-testid="bulk-groups-failed">
                    {`${formatNumber(recordingsFailed)} הקלטות לא נשמרו להאזנה. הקבצים עצמם תקינים, אבל את הקבוצות האלה אי אפשר לשמוע כאן.`}
                </Alert>
            )}

            {marked.length > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 1, p: 1, border: 1, borderColor: 'warning.main', borderRadius: 1 }} data-testid="bulk-marked-bar">
                    <Typography variant="body2" data-testid="bulk-marked-count">{marked.length === 1 ? 'סומנה מילה אחת שנשמעת לא טוב' : `סומנו ${wordsCount(marked.length)} שנשמעות לא טוב`}</Typography>
                    <Button size="small" variant="contained" onClick={() => setShowMarked(true)} data-testid="bulk-marked-show">הצגה והעתקה</Button>
                    <Button size="small" color="warning" onClick={() => setClearing(true)} data-testid="bulk-marked-clear">ניקוי הסימונים</Button>
                </Box>
            )}

            <div data-testid="bulk-groups-list">
                {groups.map((group) => (
                    <Accordion
                        key={group.number}
                        expanded={expanded === group.number}
                        onChange={(event, isOpen) => setExpanded(isOpen ? group.number : null)}
                        disableGutters
                        variant="outlined"
                        data-testid={`bulk-group-${group.number}`}
                    >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-label={groupTitle(group)}>
                            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                                <Typography sx={{ fontWeight: 'bold' }}>{groupTitle(group)}</Typography>
                                <Typography variant="body2" color="text.secondary" data-testid="bulk-group-facts"><Facts facts={groupFacts(group)} /></Typography>
                                {markedIn(group.number) > 0 && <Chip size="small" color="warning" label={markedIn(group.number) === 1 ? 'מילה אחת מסומנת' : `${wordsCount(markedIn(group.number))} מסומנות`} />}
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                            {expanded === group.number && (
                                <GroupPlayer
                                    jobId={jobId}
                                    number={group.number}
                                    hasNext={groups.some((other) => other.number === group.number + 1)}
                                    marks={marks}
                                    onToggleMark={toggleMark}
                                    onNext={() => setExpanded(group.number + 1)}
                                />
                            )}
                        </AccordionDetails>
                    </Accordion>
                ))}
            </div>

            <MarkedDialog open={showMarked} text={markedWordsText(jobId, marks)} onClose={() => setShowMarked(false)} />
            <ConfirmDialog
                open={clearing}
                title="לנקות את כל הסימונים?"
                confirmLabel="ניקוי"
                onCancel={() => setClearing(false)}
                onConfirm={() => { setMarks({}); setClearing(false); }}
                testId="bulk-marked-confirm-clear"
            >
                <Typography>
                    {`${marked.length === 1 ? 'הסימון' : `${formatNumber(marked.length)} הסימונים`} של ההרצה הזאת ${marked.length === 1 ? 'יימחק' : 'יימחקו'} מהדפדפן. ההקלטות והקבצים לא ישתנו.`}
                </Typography>
            </ConfirmDialog>
        </Box>
    );
};

export default BulkRunGroupRecordings;
